import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Check, ArrowRight, Anchor, Wrench, User, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "services", label: "Servicios y zona", icon: Wrench },
  { key: "profile", label: "Tu perfil", icon: User },
  { key: "payouts", label: "Cobros", icon: CreditCard },
  { key: "kyc", label: "Verificación", icon: ShieldCheck },
];

export default function ProOnboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [saving, setSaving] = useState(false);

  const prof = user?.professional || {};
  const [f, setF] = useState({
    services: prof.services || [],
    coverage_mode: prof.coverage_mode === "comarca" ? "comarcas" : (prof.coverage_mode || "all"),
    coverage_provinces: prof.coverage_provinces || [],
    coverage_comarcas: prof.coverage_comarcas || [],
    coverage_ports: prof.coverage_ports || [],
    bio: prof.bio || "",
    years_experience: prof.years_experience || 0,
    insurance_number: prof.insurance_number || "",
    languages: prof.languages || ["ES"],
    specialties: prof.specialties || [],
  });

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data));
    api.get("/geo/provinces").then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const currentProvs = useMemo(() => provinces.filter(p => f.coverage_provinces.includes(p.name)), [provinces, f.coverage_provinces]);
  const availComarcas = useMemo(() => [...new Set(currentProvs.flatMap(p => p.comarcas || []))], [currentProvs]);
  const availPorts = useMemo(() => [...new Set(currentProvs.flatMap(p => (p.ports || []).map(pt => typeof pt === "string" ? pt : pt.name)))], [currentProvs]);

  // Progress calculation
  const done = {
    services: (f.services || []).length > 0,
    profile: (f.bio || "").length >= 30,
    payouts: !!prof.stripe_payouts_enabled,
    kyc: prof.kyc_status === "verified",
  };
  const percent = Math.round((Object.values(done).filter(Boolean).length / 4) * 100);

  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const saveServicesStep = async () => {
    if (!f.services.length) return toast.error("Elige al menos un servicio");
    if (f.coverage_mode !== "all" && f.coverage_provinces.length === 0) return toast.error("Elige al menos una provincia");
    setSaving(true);
    try {
      await api.patch("/professionals/me", {
        services: f.services, coverage_mode: f.coverage_mode,
        coverage_provinces: f.coverage_provinces, coverage_comarcas: f.coverage_comarcas, coverage_ports: f.coverage_ports,
      });
      await (refresh && refresh());
      toast.success("Servicios guardados");
      setStep(1);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const saveProfileStep = async () => {
    if ((f.bio || "").length < 30) return toast.error("Cuenta un poco más sobre ti (mín 30 caracteres)");
    setSaving(true);
    try {
      await api.patch("/professionals/me", {
        bio: f.bio, years_experience: parseInt(f.years_experience) || 0,
        insurance_number: f.insurance_number, languages: f.languages, specialties: f.specialties,
      });
      await (refresh && refresh());
      toast.success("Perfil guardado");
      setStep(2);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const skipStep = () => setStep(Math.min(3, step + 1));
  const skipAll = () => nav("/pro");

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-serif text-3xl md:text-4xl text-[#0B1D3A]">Vamos a lanzarte, {user?.name?.split(" ")[0]}</h1>
            <button onClick={skipAll} className="text-xs text-[#5C6B7D] underline font-display" data-testid="onboarding-skip-all">
              Completar después
            </button>
          </div>
          <p className="text-[#5C6B7D] mb-4">Ya puedes recibir trabajos. Cada paso que completes te acerca al 100% y a más ofertas.</p>
          <div className="hub-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-display text-xs uppercase tracking-widest text-[#5C6B7D]">Progreso</span>
              <span className="font-display text-lg text-[#FF6B57]" data-testid="onboarding-progress">{percent}%</span>
            </div>
            <div className="h-2 bg-[#EFE9E1] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0B1D3A] to-[#FF6B57] transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon; const isDone = done[s.key]; const isActive = i === step;
                return (
                  <button key={s.key} onClick={() => setStep(i)} data-testid={`onboarding-step-${s.key}`}
                    className={`p-2 rounded-lg text-center transition-colors ${isActive ? "bg-[#0B1D3A] text-white" : isDone ? "bg-emerald-50 text-emerald-700" : "bg-[#EFE9E1] text-[#5C6B7D]"}`}>
                    <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-display">
                      {isDone ? <Check size={11}/> : <Icon size={11}/>} {s.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="hub-card p-6 md:p-8">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-serif text-2xl mb-1">1. ¿Qué servicios ofreces?</h2>
                <p className="text-sm text-[#5C6B7D]">Puedes marcar varios. Podrás cambiarlos cuando quieras.</p>
                <div className="mt-3 flex flex-wrap gap-2 p-3 rounded-lg border border-[#0B1D3A]/15 max-h-48 overflow-y-auto">
                  {services.map(s => {
                    const on = f.services.includes(s.id);
                    return (
                      <button key={s.id} type="button" data-testid={`onb-svc-${s.id}`}
                        onClick={() => setF({ ...f, services: toggle(f.services, s.id) })}
                        className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <h2 className="font-serif text-2xl mb-1 mt-4">2. ¿Dónde trabajas?</h2>
                <div className="mt-2 grid grid-cols-4 gap-1 p-1 bg-white border border-[#0B1D3A]/15 rounded-full">
                  {[{k:"all",l:"Toda España"},{k:"provinces",l:"Provincias"},{k:"comarcas",l:"Comarcas"},{k:"ports",l:"Puertos"}].map(({k,l}) => (
                    <button key={k} type="button"
                      onClick={() => setF({ ...f, coverage_mode: k, coverage_provinces: k === "all" ? [] : f.coverage_provinces, coverage_comarcas: k === "comarcas" ? f.coverage_comarcas : [], coverage_ports: k === "ports" ? f.coverage_ports : [] })}
                      data-testid={`onb-cov-${k}`}
                      className={`text-xs py-2 rounded-full font-display font-semibold ${f.coverage_mode === k ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {f.coverage_mode !== "all" && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-1.5">Provincias</div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 rounded-lg border border-[#0B1D3A]/15">
                      {provinces.map(p => {
                        const on = f.coverage_provinces.includes(p.name);
                        return (
                          <button key={p.name} type="button" data-testid={`onb-prov-${p.name}`}
                            onClick={() => setF({ ...f, coverage_provinces: toggle(f.coverage_provinces, p.name) })}
                            className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {f.coverage_mode === "comarcas" && f.coverage_provinces.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-1.5">Comarcas</div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 rounded-lg border border-[#0B1D3A]/15">
                      {availComarcas.map(c => {
                        const on = f.coverage_comarcas.includes(c);
                        return (
                          <button key={c} type="button" onClick={() => setF({ ...f, coverage_comarcas: toggle(f.coverage_comarcas, c) })}
                            className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>{c}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {f.coverage_mode === "ports" && f.coverage_provinces.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-1.5">Puertos</div>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 rounded-lg border border-[#0B1D3A]/15">
                      {availPorts.map(p => {
                        const on = f.coverage_ports.includes(p);
                        return (
                          <button key={p} type="button" onClick={() => setF({ ...f, coverage_ports: toggle(f.coverage_ports, p) })}
                            className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#FF6B57] text-white border-[#FF6B57]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>{p}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={saveServicesStep} disabled={saving} className="hub-btn-primary rounded-full flex-1 h-11 gap-2" data-testid="onb-services-save">
                  {saving ? "Guardando..." : "Continuar"} <ArrowRight size={14}/>
                </Button>
                <Button variant="ghost" onClick={skipStep} className="rounded-full h-11" data-testid="onb-services-skip">Saltar</Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">2. Tu perfil visible</h2>
              <p className="text-sm text-[#5C6B7D]">Los propietarios verán esto. Cuanto más completo, más ofertas ganarás.</p>
              <div>
                <Label>Sobre ti / tu negocio</Label>
                <Textarea value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })}
                  placeholder="Ej: 15 años reparando motores diésel en Palma. Servicio técnico oficial Volvo Penta. Trabajo con yates de hasta 20m..."
                  className="min-h-[100px]" data-testid="onb-bio"/>
                <div className="text-xs text-[#5C6B7D] mt-1">{f.bio.length} caracteres (mín. 30)</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Años de experiencia</Label>
                  <Input type="number" min="0" max="60" value={f.years_experience} onChange={e => setF({ ...f, years_experience: e.target.value })} data-testid="onb-years"/>
                </div>
                <div>
                  <Label>Nº seguro RC (opcional)</Label>
                  <Input value={f.insurance_number} onChange={e => setF({ ...f, insurance_number: e.target.value })} placeholder="Ej: 12345678" data-testid="onb-insurance"/>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Idiomas</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {["ES","EN","FR","DE","IT","CA"].map(l => {
                    const on = (f.languages || []).includes(l);
                    return (
                      <button key={l} type="button" onClick={() => setF({ ...f, languages: toggle(f.languages || [], l) })}
                        className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15"}`}>{l}</button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={saveProfileStep} disabled={saving} className="hub-btn-primary rounded-full flex-1 h-11 gap-2" data-testid="onb-profile-save">
                  {saving ? "..." : "Continuar"} <ArrowRight size={14}/>
                </Button>
                <Button variant="ghost" onClick={skipStep} className="rounded-full h-11" data-testid="onb-profile-skip">Saltar</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">3. Cobros</h2>
              <p className="text-[#5C6B7D]">Configura tu cuenta bancaria con Stripe para recibir automáticamente los pagos cuando el cliente apruebe tu trabajo.</p>
              {done.payouts ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-2 text-sm text-emerald-700">
                  <Check size={16}/> Cobros ya configurados. Podrás recibir pagos automáticos.
                </div>
              ) : (
                <div className="hub-navy rounded-xl p-5 text-white">
                  <p className="text-sm text-white/80 mb-3">Sin configurar cobros, el pago del cliente queda seguro en la plataforma y te lo transferimos manualmente al aprobar el trabajo. Configurar Stripe hace que llegue solo en 24h.</p>
                  <Button onClick={() => nav("/pro/connect")} className="hub-btn-coral hub-btn-primary rounded-full gap-2" data-testid="onb-stripe-btn">
                    <CreditCard size={14}/> Configurar Stripe ahora
                  </Button>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button variant="ghost" onClick={skipStep} className="rounded-full h-11 flex-1" data-testid="onb-payouts-skip">Continuar sin configurar</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">4. Verificación KYC</h2>
              <p className="text-[#5C6B7D]">Sube tu DNI o CIF de empresa. Los perfiles verificados ganan el <b>60% más de ofertas</b>. Solo lo ve el equipo de MarinaHub — nunca los clientes.</p>
              {done.kyc ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-2 text-sm text-emerald-700">
                  <Check size={16}/> Perfil verificado. Aparecerá el badge KYC en tu tarjeta.
                </div>
              ) : (
                <Button onClick={() => nav("/pro/kyc")} className="hub-btn-primary rounded-full gap-2 w-full h-11" data-testid="onb-kyc-btn">
                  <ShieldCheck size={14}/> Enviar documentos ahora
                </Button>
              )}
              <Button onClick={() => nav("/pro")} variant="ghost" className="rounded-full h-11 w-full" data-testid="onb-finish">
                Ir a mi panel {percent === 100 ? "🎉" : ""}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
